import { File, Share } from "@styled-icons/boxicons-solid";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";
import { Channel, Message, User } from "revolt.js";
import styled, { css } from "styled-components/macro";
import { Text } from "preact-i18n";
import { useLayoutEffect, useRef, useState } from "preact/hooks";

import { internalEmit } from "../../../../lib/eventEmitter";
import { getRenderer } from "../../../../lib/renderer/Singleton";
import { TIME } from "../../../../lib/time/time";

import Markdown from "../../../markdown/Markdown";
import UserShort from "../../user/UserShort";
import { SystemMessage } from "../SystemMessage";

interface ChannelReference {
    name: string;
    type?: string;
    channel_type?: string;
    server_name?: string | null;
    category_name?: string | null;
}

interface Props {
    channel?: Channel;
    index: number;
    id?: string;
    type?: "reply" | "forward";
    forwardId?: string;
    forwardChannelId?: string;
    forwardAuthorId?: string;
}

export const ReplyBase = styled.div<{
    head?: boolean;
    fail?: boolean;
    noBorder?: boolean;
    forward?: boolean;
}>`
    gap: 8px;
    min-width: 0;
    display: flex;
    margin-inline-start: 30px;
    margin-inline-end: 12px;
    font-size: 0.8em;
    user-select: none;
    align-items: end;
    color: var(--secondary-foreground);

    &::before {
        content: "";
        flex-shrink: 0;
        width: 22px;
        height: 10px;
        border-inline-start: 2px solid var(--message-box);
        border-top: 2px solid var(--message-box);
        align-self: flex-end;
    }

    * {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .user {
        gap: 6px;
        display: flex;
        flex-shrink: 0;
        font-weight: 600;
        overflow: visible;
        align-items: center;
        padding: 2px 0;

        ${(props) =>
            props.forward &&
            css`
                font-size: var(--text-size);
            `}

        span {
            cursor: pointer;
            &:hover {
                text-decoration: underline;
            }
        }

        .forward-source {
            white-space: normal;
            overflow: visible;
        }

        ${(props) =>
            props.fail &&
            css`
                .forward-source {
                    white-space: normal;
                    overflow: visible;
                    span {
                        cursor: default;
                        &:hover {
                            text-decoration: none;
                        }
                    }
                }
            `}
    }

    .content {
        max-height: 32px;

        gap: 4px;
        display: flex;
        padding: 2px 0;
        cursor: pointer;
        overflow: hidden;
        align-items: center;
        flex-direction: row;

        transition: filter 1s ease-in-out;
        transition: transform ease-in-out 0.1s;
        filter: brightness(1);

        > svg {
            flex-shrink: 0;
        }

        > span > p {
            display: flex;
            align-items: center;
            gap: 4px;
        }

        &:hover {
            filter: brightness(2);
        }

        &:active {
            transform: translateY(1px);
        }

        > * {
            pointer-events: none;
        }
    }

    > svg:first-child {
        flex-shrink: 0;
        color: var(--tertiary-foreground);
    }

    ${(props) =>
        props.fail &&
        css`
            color: var(--tertiary-foreground);
        `}

    ${(props) =>
        props.head &&
        css`
            /* margin-top: 12px; */
            padding-top: 12px;

            &::before {
                border-start-start-radius: 4px;
            }
        `}

    ${(props) =>
        props.noBorder &&
        css`
            &::before {
                display: none;
            }
            margin-inline-start: 0;
            margin-top: 0;

            > svg:first-child {
                transform: scaleX(-1);
            }
        `}
`;

export const ForwardWrapper = styled.div<{ head?: boolean; active?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 0;

    ${(props) =>
        props.active &&
        css`
            margin-inline-start: -12px;
            margin-inline-end: -12px;
            padding-left: 0;
            border-left: 2px solid var(--message-box);

            ${
                props.head &&
                css`
                    /* margin-top: 12px; */
                    padding-top: 12px;
                    border-top-left-radius: 4px;
                `
            }
        `}
`;

export const ForwardBody = styled.div<{ active?: boolean }>`
    ${(props) =>
        props.active &&
        css`
            margin-inline-start: 42px;
        `}
`;

export const MessageReply = observer(
    ({
        index,
        channel,
        id,
        type = "reply",
        forwardId,
        forwardChannelId,
        forwardAuthorId,
    }: Props) => {
        const isForward = type === "forward";
        const history = useHistory();

        // Reply mode: need channel and id
        if (!isForward) {
            if (!channel || !id) return null;
            const view = getRenderer(channel);
            if (view.state !== "RENDER") return null;

            return (
                <ReplyMode
                    index={index}
                    channel={channel}
                    id={id}
                    view={view}
                    history={history}
                />
            );
        }

        // Forward mode
        return (
            <ForwardMode
                index={index}
                forwardId={forwardId}
                forwardChannelId={forwardChannelId}
                forwardAuthorId={forwardAuthorId}
                channel={channel}
                history={history}
            />
        );
    },
);

export const PreviewContent = ({
    message,
    userSize,
    onClick,
    showContent,
    afterAuthor,
}: {
    message: Message;
    userSize?: number;
    onClick?: () => void;
    showContent?: boolean;
    afterAuthor?: any;
}) => {
    if (message.author?.relationship === "Blocked") {
        return <Text id="app.main.channel.misc.blocked_user" />;
    }

    if (message.author_id === "00000000000000000000000000") {
        return <SystemMessage message={message} hideInfo />;
    }

    return (
        <>
            <div className="user" onClick={onClick}>
                <UserShort
                    size={userSize ?? 14}
                    showServerIdentity
                    user={message.author}
                    masquerade={message.masquerade!}
                />
                {afterAuthor}
            </div>
            {(showContent ?? true) && (
                <div className="content" onClick={onClick}>
                    {message.attachments && (
                        <>
                            <File size={16} />
                            <em>
                                {message.attachments.length > 1 ? (
                                    <Text id="app.main.channel.misc.sent_multiple_files" />
                                ) : (
                                    <Text id="app.main.channel.misc.sent_file" />
                                )}
                            </em>
                        </>
                    )}
                    {message.content && (
                        <Markdown
                            disallowBigEmoji
                            content={message.content.replace(/\n/g, " ")}
                            channel={message.channel}
                        />
                    )}
                </div>
            )}
        </>
    );
};

const ForwardSource = observer(
    ({
        fwdChannel,
        forwardId,
        history,
    }: {
        fwdChannel: Channel;
        forwardId?: string;
        history?: ReturnType<typeof useHistory>;
    }) => {
        const content = (() => {
            if (fwdChannel.channel_type === "Group") {
                return (
                    <>
                        <Text id="app.main.channel.misc.forward_in" />{" "}
                        {fwdChannel.name}
                    </>
                );
            }
            const server = fwdChannel.server;
            const categoryName = server?.categories?.find((c) =>
                c.channels?.includes(fwdChannel._id),
            )?.title;
            return (
                <>
                    <Text id="app.main.channel.misc.forward_in" />{" "}
                    {server?.name && <>{server.name}/</>}
                    {categoryName && <>{categoryName}/</>}
                    {fwdChannel.name}
                </>
            );
        })();

        if (
            fwdChannel.channel_type === "DirectMessage" ||
            fwdChannel.channel_type === "SavedMessages"
        ) {
            return null;
        }

        if (!history) return <>{content}</>;

        const navigate = () => {
            const path = forwardId
                ? `${fwdChannel.path}/${forwardId}`
                : fwdChannel.path;
            history.push(path);
        };
        return (
            <span onClick={navigate} style={{ cursor: "pointer" }}>
                {content}
            </span>
        );
    },
);

const ForwardSourceReference = observer(
    ({ refData }: { refData: ChannelReference }) => {
        if (!refData) return null;
        const source = (() => {
            const chType = refData.type ?? refData.channel_type;
            if (chType === "group") {
                return (
                    <>
                        <Text id="app.main.channel.misc.forward_in" />{" "}
                        {refData.name}
                    </>
                );
            }
            if (chType === "direct_message") {
                return null;
            }
            return (
                <>
                    <Text id="app.main.channel.misc.forward_in" />{" "}
                    {refData.server_name && <>{refData.server_name}/</>}
                    {refData.category_name && <>{refData.category_name}/</>}
                    {refData.name}
                </>
            );
        })();
        return source;
    },
);

const MessagePreview = observer(
    ({
        message,
        Icon,
        onClick,
        head,
        fail,
        noBorder,
        forward,
        forwardAuthor,
        fwdChannel,
        channelRef,
        forwardId,
        history,
    }: {
        message?: Message;
        Icon?: any;
        onClick?: () => void;
        head: boolean;
        fail?: boolean;
        noBorder?: boolean;
        forward?: boolean;
        forwardAuthor?: User;
        fwdChannel?: Channel;
        channelRef?: ChannelReference;
        forwardId?: string;
        history?: ReturnType<typeof useHistory>;
    }) => {
        const hasFullChannel =
            fwdChannel &&
            ((fwdChannel.server && fwdChannel.server.name) ||
                (fwdChannel.channel_type === "Group" && fwdChannel.name));

        if (!message) {
            if (forward && (forwardAuthor || fwdChannel || channelRef)) {
                return (
                    <ReplyBase
                        head={head}
                        fail
                        noBorder={noBorder}
                        forward={forward}>
                        {Icon && <Icon size={16} />}
                        <div className="user">
                            <UserShort
                                size={14}
                                user={forwardAuthor}
                                showServerIdentity
                            />
                            <div className="forward-source">
                                {hasFullChannel && (
                                    <ForwardSource
                                        fwdChannel={fwdChannel}
                                        forwardId={forwardId}
                                    />
                                )}
                                {channelRef && !hasFullChannel && (
                                    <ForwardSourceReference
                                        refData={channelRef}
                                    />
                                )}
                            </div>
                        </div>
                    </ReplyBase>
                );
            }
            return (
                <ReplyBase
                    head={head}
                    fail
                    noBorder={noBorder}
                    forward={forward}>
                    {Icon && <Icon size={16} />}
                    <span>
                        <Text id="app.main.channel.misc.failed_load" />
                        (недоступно)
                    </span>
                </ReplyBase>
            );
        }
        return (
            <ReplyBase head={head} noBorder={noBorder} forward={forward}>
                {Icon && (
                    <Icon
                        size={16}
                        style={{ cursor: "pointer" }}
                        onClick={onClick}
                    />
                )}
                <PreviewContent
                    message={message}
                    onClick={onClick}
                    showContent={!noBorder}
                    afterAuthor={
                        forward && fwdChannel && history ? (
                            <ForwardSource
                                fwdChannel={fwdChannel}
                                forwardId={forwardId}
                                history={history}
                            />
                        ) : undefined
                    }
                />
            </ReplyBase>
        );
    },
);

const navigateToMessage = (
    msg: Message,
    history: ReturnType<typeof useHistory>,
) => {
    const ch = msg.channel;
    if (!ch) return;
    if (ch.channel_type === "TextChannel") {
        history.push(`/server/${ch.server_id}/channel/${ch._id}/${msg._id}`);
    } else {
        history.push(`/channel/${ch._id}/${msg._id}`);
    }
};

const useVerifyNavigate = (
    setMessage: (v: Message | undefined) => void,
    history: ReturnType<typeof useHistory>,
) => {
    const mounted = useRef(true);
    useLayoutEffect(
        () => () => {
            mounted.current = false;
        },
        [],
    );
    return (message: Message | undefined) => {
        if (!message || !mounted.current) return;
        message
            .channel!.fetchMessage(message._id)
            .then((msg) => {
                if (mounted.current) navigateToMessage(msg, history);
            })
            .catch((err) => {
                if (!mounted.current) return;
                if (err.response?.status === 404) {
                    setMessage(undefined);
                    internalEmit("System", "alert", {
                        text: "Сообщение было удалено.",
                        dismissable: true,
                        timeout: TIME.SECOND * 3,
                    });
                }
            });
    };
};

const ReplyMode = observer(
    ({
        index,
        channel,
        id,
        view,
        history,
    }: {
        index: number;
        channel: Channel;
        id: string;
        view: ReturnType<typeof getRenderer>;
        history: ReturnType<typeof useHistory>;
    }) => {
        const [message, setMessage] = useState<Message | undefined>(undefined);
        const verifyNav = useVerifyNavigate(setMessage, history);

        useLayoutEffect(() => {
            const message = channel.client.messages.get(id);
            if (message) {
                setMessage(message);
            } else {
                channel.fetchMessage(id).then(setMessage);
            }
        }, [id, channel, view.messages]);

        return (
            <MessagePreview
                message={message}
                head={index === 0}
                onClick={() => verifyNav(message)}
            />
        );
    },
);

const ForwardMode = observer(
    ({
        index,
        forwardId,
        forwardChannelId,
        forwardAuthorId,
        channel,
        history,
    }: {
        index: number;
        forwardId?: string;
        forwardChannelId?: string;
        forwardAuthorId?: string;
        channel?: Channel;
        history: ReturnType<typeof useHistory>;
    }) => {
        const client = channel?.client;
        const [message, setMessage] = useState<Message | undefined>(undefined);
        const [channelRef, setChannelRef] = useState<
            ChannelReference | undefined
        >(undefined);
        const verifyNav = useVerifyNavigate(setMessage, history);

        // Fetch forward channel from API if not in MobX cache;
        // observer tracks client.channels.get() below and re-renders when it appears
        useLayoutEffect(() => {
            if (!client || !forwardId || !forwardChannelId) return;

            if (forwardAuthorId && !client.users.get(forwardAuthorId)) {
                client.users.fetch(forwardAuthorId).catch(() => {});
            }

            const cached = client.channels.get(forwardChannelId);
            if (cached) {
                cached
                    .fetchMessage(forwardId)
                    .then(setMessage)
                    .catch(() => {
                        // Message not readable — try reference fallback
                        client.channels
                            .fetchReference(forwardChannelId)
                            .then((ref) =>
                                setChannelRef(ref as ChannelReference),
                            )
                            .catch(() => {});
                    });
                return;
            }
            client.channels
                .fetch(forwardChannelId)
                .then((ch) => {
                    ch.fetchMessage(forwardId)
                        .then(setMessage)
                        .catch(() => {
                            // Message not readable — try reference fallback
                            client.channels
                                .fetchReference(forwardChannelId)
                                .then((ref) =>
                                    setChannelRef(ref as ChannelReference),
                                )
                                .catch(() => {});
                        });
                    if (
                        ch.channel_type === "TextChannel" &&
                        ch.server_id &&
                        !client.servers.get(ch.server_id)
                    ) {
                        client.servers.fetch(ch.server_id).catch(() => {});
                    }
                })
                .catch(() => {
                    // Channel not accessible — fetch reference instead
                    client.channels
                        .fetchReference(forwardChannelId)
                        .then((ref) => setChannelRef(ref as ChannelReference))
                        .catch(() => {});
                });
        }, [forwardId, forwardChannelId, forwardAuthorId, client, channel]);

        if (!forwardId) return null;

        // Resolve from MobX cache — observer re-renders when entries appear
        const forwardAuthor = forwardAuthorId
            ? client?.users.get(forwardAuthorId)
            : undefined;
        const fwdChannel = forwardChannelId
            ? client?.channels.get(forwardChannelId)
            : undefined;

        return (
            <MessagePreview
                message={message}
                Icon={Share}
                head={index === 0}
                noBorder
                forward
                onClick={() => verifyNav(message)}
                forwardAuthor={forwardAuthor}
                fwdChannel={fwdChannel}
                channelRef={channelRef}
                forwardId={forwardId}
                history={history}
            />
        );
    },
);
